import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Loader2, IndianRupee, TrendingUp, Receipt } from 'lucide-react';

const statusColor = (s) => ({ paid: 'bg-green-100 text-green-800', created: 'bg-yellow-100 text-yellow-800', failed: 'bg-red-100 text-red-800' }[s] || 'bg-gray-100 text-gray-700');
const purposeLabel = (p) => ({ membership: 'Membership', booking: 'Booking', custom: 'Other' }[p] || p);

const Stat = ({ label, value, icon: Icon, gradient, testid }) => (
  <Card className={`border-0 shadow-sm bg-gradient-to-r ${gradient} text-white`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div><p className="text-white/80 text-sm">{label}</p><p className="text-2xl font-bold" data-testid={testid}>{value}</p></div>
        <Icon className="h-7 w-7 opacity-80" />
      </div>
    </CardContent>
  </Card>
);

const PaymentsPage = () => {
  const [revenue, setRevenue] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [rev, pay] = await Promise.all([
        api.get('/payments/revenue-summary'),
        api.get('/payments', { params: { limit: 100 } }),
      ]);
      setRevenue(rev.data); setPayments(pay.data);
    } catch (err) {
      toast({ title: 'Failed to load payments', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="admin-payments">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments & Revenue</h1>
          <p className="text-gray-500 text-sm mt-1">Transactions and invoices</p>
        </div>
        {revenue?.mock_mode && <Badge className="bg-amber-100 text-amber-800" data-testid="mock-mode-badge">Razorpay Test/Mock Mode</Badge>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Revenue" value={`\u20B9${(revenue?.total_revenue ?? 0).toLocaleString('en-IN')}`} icon={IndianRupee} gradient="from-emerald-600 to-green-600" testid="pay-total" />
        <Stat label="Today" value={`\u20B9${(revenue?.today_revenue ?? 0).toLocaleString('en-IN')}`} icon={TrendingUp} gradient="from-blue-600 to-cyan-600" testid="pay-today" />
        <Stat label="Transactions" value={revenue?.transactions ?? 0} icon={Receipt} gradient="from-purple-600 to-pink-600" testid="pay-txns" />
        <Stat label="Memberships" value={`\u20B9${(revenue?.by_purpose?.membership ?? 0).toLocaleString('en-IN')}`} icon={IndianRupee} gradient="from-indigo-600 to-blue-600" testid="pay-membership-rev" />
      </div>

      <Card className="shadow-md border-0 bg-white">
        <CardContent className="p-0">
          {payments.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="payments-empty">No payments yet.</div>
          ) : (
            <Table data-testid="payments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.invoice_number || '—'}</TableCell>
                    <TableCell className="font-medium">{p.customer_name || p.swimmer_name || '—'}</TableCell>
                    <TableCell>{purposeLabel(p.purpose)}{p.plan_name ? ` · ${p.plan_name}` : ''}</TableCell>
                    <TableCell className="font-semibold">{'\u20B9'}{(p.amount || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell><Badge className={statusColor(p.status)}>{p.status}</Badge></TableCell>
                    <TableCell className="text-gray-500 text-sm">{(p.paid_at || p.created_at || '').slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
